// @warp-css;
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { reset } from "../styles.js";
import { styles } from "./styles.js";
import { inputLabelStyles, inputHelpTextStyles } from "./input-styles.js";
import { activateI18n } from "../i18n.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import "../icon/icon.js";
import "../tooltip/tooltip.js";
// NOTE: Label and help-text are rendered inline using shared input styles.
// In a future major version, we could extract these into separate w-label and w-help-text components
// if we find significant reuse opportunities across non-input components.
/**
 * A multi-line text input with built-in form validation, auto-resizing, and styling support.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/text-area/frameworks/elements)
 */
class WarpTextarea extends FormControlMixin(LitElement) {
    /** @internal */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    get #hasHelpText() {
        return typeof this.helpText !== "undefined" || this._hasHelpTextSlot;
    }
    // capture the initial value using connectedCallback and #initialValue
    #initialValue;
    // Track whether the current invalid/helpText state was set by validation
    #validationActive;
    // Store the original helpText to restore when validation passes
    #originalHelpText;
    // Track whether the user has interacted with the field
    #hasInteracted;
    // Stores the explicit message set through setCustomValidity.
    #customValidationMessage;
    constructor() {
        super();
        /**
         * Keep in mind that using disabled in its current form is an anti-pattern.
         *
         * There will always be users who don't understand why an element is disabled, or users who can't even see that it is disabled because of poor lighting conditions or other reasons.
         *
         * Please consider more informative alternatives before choosing to use disabled on an element.
         *
         * @summary Makes the element not focusable and hides it from form submits
         */
        this.disabled = false;
        /**
         * Mark the form field as invalid.
         *
         * Make sure to also set a `help-text` to help users fix the validation problem.
         */
        this.invalid = false;
        /**
         * @deprecated Use the native `readonly` attribute instead
         */
        this.readOnly = false;
        /**
         * Whether the input can be selected but not changed by the user
         */
        this.readonly = false;
        /**
         * Whether user input is required on the input before form submission
         */
        this.required = false;
        /**
         * Show an icon behind the label indicating the field is optional
         */
        this.optional = false;
        this.minHeight = Number.NEGATIVE_INFINITY;
        this.maxHeight = Number.POSITIVE_INFINITY;
        this._hasHelpTextSlot = false;
        // capture the initial value using connectedCallback and #initialValue
        this.#initialValue = undefined;
        // Track whether the current invalid/helpText state was set by validation
        this.#validationActive = false;
        // Store the original helpText to restore when validation passes
        this.#originalHelpText = undefined;
        // Track whether the user has interacted with the field
        this.#hasInteracted = false;
        // Stores the explicit message set through setCustomValidity.
        this.#customValidationMessage = "";
        /** @internal */
        this.#handleInvalid = (e) => {
            // Prevent browser's native validation bubble
            e.preventDefault();
            // Mark as interacted and show validation state
            this.#hasInteracted = true;
            this.#updateValidity();
        };
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
    }
    updated(changedProperties) {
        if (changedProperties.has("value") && typeof this.value !== "undefined") {
            this.setValue(this.value);
        }
        if (changedProperties.has("value") ||
            changedProperties.has("required") ||
            changedProperties.has("disabled") ||
            changedProperties.has("minLength") ||
            changedProperties.has("maxLength")) {
            this.#updateValidity();
        }
    }
    resetFormControl() {
        this.value = this.#initialValue;
        this.#hasInteracted = false;
        this.#clearValidationState();
        this.#updateValidity();
    }
    /** Returns the validation message if the textarea is invalid, otherwise an empty string */
    get validationMessage() {
        return this.internals.validationMessage;
    }
    /** Returns the validity state of the textarea */
    get validity() {
        return this.internals.validity;
    }
    /** Checks whether the textarea passes constraint validation */
    checkValidity() {
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** Checks validity and shows the validation message if invalid */
    reportValidity() {
        this.#hasInteracted = true;
        this.#updateValidity();
        return this.internals.checkValidity();
    }
    /** Sets a custom validation message. Pass an empty string to clear. */
    setCustomValidity(message) {
        this.#customValidationMessage = message;
        if (message) {
            this.#updateValidity();
            this.#setValidationState(message);
        }
        else {
            this.#clearValidationState();
            this.#updateValidity();
        }
    }
    /** @internal */
    #setValidationState(message) {
        if (!this.#validationActive) {
            this.#originalHelpText = this.helpText;
        }
        this.#validationActive = true;
        this.invalid = true;
        this.helpText = message;
    }
    /** @internal */
    #clearValidationState() {
        if (this.#validationActive) {
            this.invalid = false;
            this.helpText = this.#originalHelpText;
            this.#originalHelpText = undefined;
            this.#validationActive = false;
        }
    }
    /** @internal */
    #updateValidity() {
        if (!this._textarea) {
            return;
        }
        // Skip validation if disabled
        if (this.disabled) {
            this.internals.setValidity({});
            this.#clearValidationState();
            return;
        }
        const validity = this._textarea.validity;
        const value = this.value ?? this._textarea.value ?? "";
        const tooShort = value.length > 0 &&
            typeof this.minLength === "number" &&
            value.length < this.minLength;
        const tooLong = typeof this.maxLength === "number" && value.length > this.maxLength;
        const flags = {
            valueMissing: validity.valueMissing,
            tooShort: validity.tooShort || tooShort,
            tooLong: validity.tooLong || tooLong,
            customError: this.#customValidationMessage !== "",
        };
        if (Object.values(flags).some(Boolean)) {
            const message = this.#getValidationMessage(flags, value.length);
            this.internals.setValidity(flags, message, this._textarea);
            if (this.#hasInteracted) {
                this.#setValidationState(message);
            }
            return;
        }
        this.internals.setValidity({});
        this.#clearValidationState();
    }
    #getValidationMessage(flags, currentLength) {
        if (flags.customError && this.#customValidationMessage) {
            return this.#customValidationMessage;
        }
        if (flags.valueMissing) {
            return i18n._({
                id: "textarea.validation.valueMissing",
                message: "Please fill out this field.",
                comment: "Validation message shown when textarea value is required",
            });
        }
        if (flags.tooShort && typeof this.minLength === "number") {
            return i18n._({
                id: "textarea.validation.tooShort",
                message: "Please lengthen this text to {minLength} characters or more (you are currently using {currentLength} characters).",
                comment: "Validation message shown when textarea value is too short",
                values: { minLength: this.minLength, currentLength },
            });
        }
        if (flags.tooLong && typeof this.maxLength === "number") {
            return i18n._({
                id: "textarea.validation.tooLong",
                message: "Please shorten this text to {maxLength} characters or less (you are currently using {currentLength} characters).",
                comment: "Validation message shown when textarea value is too long",
                values: { maxLength: this.maxLength, currentLength },
            });
        }
        return i18n._({
            id: "textarea.validation.invalid",
            message: "Please enter a valid value.",
            comment: "Fallback validation message for textarea",
        });
    }
    static { this.styles = [reset, styles, inputLabelStyles, inputHelpTextStyles]; }
    /** @internal */
    get _helpId() {
        if (this.#hasHelpText)
            return `${this._id}__hint`;
        return undefined;
    }
    /** @internal */
    get _id() {
        return "textarea";
    }
    /** @internal */
    get _error() {
        if (this.invalid && this._helpId)
            return this._helpId;
        return undefined;
    }
    async connectedCallback() {
        super.connectedCallback();
        this.#initialValue = this.value;
        if (typeof this.value !== "undefined") {
            this.setValue(this.value);
        }
        // Listen for invalid event on the host element (fired by form validation)
        this.addEventListener("invalid", this.#handleInvalid);
        await this.updateComplete;
        if (this.value || this.minRows) {
            // If the component starts with a value or minHeight,
            // resize it automatically to show all the content, or maxHeight.
            const textarea = this.shadowRoot?.querySelector("textarea");
            if (textarea) {
                this.#resize(textarea);
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("invalid", this.#handleInvalid);
    }
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        // Initialize validity after the shadow DOM is ready
        this.#updateValidity();
    }
    handler(e) {
        const target = e.currentTarget;
        this.value = target.value;
        this.#resize(target);
    }
    /**
     * See:
     *  - https://github.com/warp-ds/elements/issues/722
     *  - https://github.com/lit/lit-element/issues/922
     *  - https://developer.mozilla.org/en-US/docs/Web/API/Event/composed
     *  - https://pm.dartus.fr/posts/2021/shadow-dom-and-event-propagation/
     */
    #redispatch(e) {
        // @ts-expect-error The constructor is there and usable
        this.dispatchEvent(new e.constructor(e.type, e));
    }
    /** @internal */
    #handleBlur() {
        this.#hasInteracted = true;
        this.#updateValidity();
    }
    /** @internal */
    #handleInvalid;
    /** Calculate the new height for the area on input */
    #resize(target) {
        const style = getComputedStyle(target);
        const borderTopWidth = Number.parseFloat(style.getPropertyValue("border-top-width"));
        const borderBottomWidth = Number.parseFloat(style.getPropertyValue("border-bottom-width"));
        const lineHeight = Number.parseFloat(style.getPropertyValue("line-height"));
        const topPadding = Number.parseFloat(style.getPropertyValue("padding-top"));
        const bottomPadding = Number.parseFloat(style.getPropertyValue("padding-bottom"));
        const offset = topPadding + bottomPadding + borderBottomWidth + borderTopWidth;
        if (this.minRows) {
            this.minHeight = lineHeight * this.minRows + offset;
        }
        if (this.maxRows) {
            this.maxHeight = lineHeight * this.maxRows + offset;
        }
        const borderBoxHeight = target.scrollHeight + borderTopWidth + borderBottomWidth;
        const height = Math.min(this.maxHeight, Math.max(this.minHeight, borderBoxHeight));
        target.style.setProperty("height", height + "px");
    }
    helpTextSlotChange() {
        const el = this.renderRoot.querySelector("slot[name=help-text]");
        this._hasHelpTextSlot = el.assignedElements().length > 0;
    }
    render() {
        return html `
			${this.label
            ? html `
							<label part="label" for="${this._id}">
								${this.label}
								${this.optional && !this.required
                ? html `
												<span>
													${i18n._({
                    id: "textarea.label.optional",
                    message: "Optional",
                    comment: "Shown behind label when marked as optional",
                })}
												</span>
											`
                : nothing}
								${this.tooltip
                ? html `
												<button
													id="tooltip-target"
													part="tooltip-target"
													aria-describedby="tooltip"
												>
													<w-icon name="Info" size="small"></w-icon>
												</button>
												<w-tooltip
													for="tooltip-target"
													id="tooltip"
													exportparts="tooltip, arrow, beak, hover-bridge"
												>
													${this.tooltip}
												</w-tooltip>
											`
                : nothing}
							</label>
						`
            : nothing}
			<textarea
				part="input"
				id="${this._id}"
				name="${ifDefined(this.name)}"
				placeholder="${ifDefined(this.placeholder)}"
				.value="${this.value || ""}"
				aria-describedby="${ifDefined(this._helpId ||
            (this.ariaDescription ? "aria-description" : undefined))}"
				aria-errormessage="${ifDefined(this._error)}"
				aria-invalid=${ifDefined(this.invalid ? "true" : undefined)}
				maxlength="${ifDefined(this.maxLength)}"
				minlength="${ifDefined(this.minLength)}"
				?disabled="${this.disabled}"
				?readonly="${this.readonly || this.readOnly}"
				?required="${this.required}"
				@input="${this.handler}"
				@blur="${this.#handleBlur}"
				@change="${this.#redispatch}"
			>
			</textarea>
			<div
				?hidden=${!this.#hasHelpText}
				id="${ifDefined(this._helpId)}"
				part="help-text"
				class="help-text"
			>
				${this.helpText}
				<slot @slotchange="${this.helpTextSlotChange}" name="help-text"></slot>
			</div>
		`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "invalid", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "label", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: "help-text" })
], WarpTextarea.prototype, "helpText", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "maximum-rows" })
], WarpTextarea.prototype, "maxRows", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "minimum-rows" })
], WarpTextarea.prototype, "minRows", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "maxlength" })
], WarpTextarea.prototype, "maxLength", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "minlength" })
], WarpTextarea.prototype, "minLength", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "read-only" })
], WarpTextarea.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "tooltip", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextarea.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextarea.prototype, "optional", void 0);
__decorate([
    state()
], WarpTextarea.prototype, "minHeight", void 0);
__decorate([
    state()
], WarpTextarea.prototype, "maxHeight", void 0);
__decorate([
    query("textarea")
], WarpTextarea.prototype, "_textarea", void 0);
__decorate([
    state()
], WarpTextarea.prototype, "_hasHelpTextSlot", void 0);
if (!customElements.get("w-textarea")) {
    customElements.define("w-textarea", WarpTextarea);
}
export { WarpTextarea };
//# sourceMappingURL=textarea.js.map